import React, { useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTestNavigation } from "../../../utils/testNavigation";
import { useGetTestAttemptAllResultsQuery } from "../mockTestSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HearingIcon from "@mui/icons-material/Hearing";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CreateIcon from "@mui/icons-material/Create";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GradeIcon from "@mui/icons-material/Grade";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import QuizIcon from "@mui/icons-material/Quiz";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ListeningSectionReview from "./ListeningSectionReview";
import ReadingSectionReview from "./ReadingSectionReview";
import WritingSectionReview from "./WritingSectionReview";
import SpeakingSectionReview from "./SpeakingSectionReview";
import { generateExplanationWithRetry } from "../../../utils/openaiService";

// Brand colors
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";

const moduleConfig = {
  listening: {
    key: "listeningResult",
    name: "Listening",
    icon: HearingIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
  reading: {
    key: "readingResult",
    name: "Reading",
    icon: MenuBookIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
  writing: {
    key: "writingResult",
    name: "Writing",
    icon: CreateIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
  speaking: {
    key: "speakingResult",
    name: "Speaking",
    icon: RecordVoiceOverIcon,
    color: BRAND_PRIMARY,
    bgColor: BRAND_BG,
  },
};

// Helper function to clean question text
const cleanQuestionText = (raw = "") => String(raw).replace(/\s+/g, " ").trim();

// Helper function to normalize heading
const normalizeHeading = (raw) => {
  if (!raw) return "";
  // Remove HTML tags
  const withoutTags = String(raw).replace(/<[^>]*>/g, "");
  const parts = withoutTags
    .split(/\r?\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts
    .join(" – ")
    .replace(/\s{2,}/g, " ")
    .trim();
};

// Component to render fill-in-blank question with answer display
const FillInBlankQuestion = ({
  question,
  userAnswer,
  config,
  questionNumber,
}) => {
  const isAttempted = !!userAnswer;
  const isCorrect = userAnswer?.isCorrect;

  // Replace blanks with answer display
  const renderQuestionWithAnswer = () => {
    let questionText = question.question || "";

    // Find the blank pattern (numbers in parentheses followed by underscores)
    const blankPattern = /\((\d+)\)_+/g;

    if (blankPattern.test(questionText)) {
      // Reset the regex
      blankPattern.lastIndex = 0;

      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = blankPattern.exec(questionText)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(
            <span
              key={`text-${lastIndex}`}
              dangerouslySetInnerHTML={{
                __html: questionText.slice(lastIndex, match.index),
              }}
            />
          );
        }

        // Add the answer display
        parts.push(
          <span
            key={`answer-${match.index}`}
            style={{
              fontWeight: "600",
              padding: "2px 8px",
              borderRadius: "4px",
              backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
              color: isCorrect ? "#166534" : "#991b1b",
              margin: "0 4px",
              display: "inline-block",
            }}
          >
            ({match[1]}) {userAnswer?.givenAnswer || "___"}
          </span>
        );

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < questionText.length) {
        parts.push(
          <span
            key={`text-end`}
            dangerouslySetInnerHTML={{ __html: questionText.slice(lastIndex) }}
          />
        );
      }

      return parts;
    }

    // If no blank pattern, just return the question with HTML
    return <span dangerouslySetInnerHTML={{ __html: questionText }} />;
  };

  return (
    <div
      style={{
        marginBottom: "16px",
        padding: "16px",
        borderRadius: "10px",
        backgroundColor: !isAttempted
          ? "#f8fafc"
          : isCorrect
          ? "#f0fdf4"
          : "#fef2f2",
        border: `1px solid ${
          !isAttempted ? "#e2e8f0" : isCorrect ? "#86efac" : "#fecaca"
        }`,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "28px",
            height: "28px",
            borderRadius: "50%",
            backgroundColor: config.color,
            color: "white",
            fontSize: "12px",
            fontWeight: "600",
            flexShrink: 0,
          }}
        >
          {questionNumber || question.questionId}
        </span>
        <div style={{ flex: 1 }}>
          <div
            style={{ fontSize: "14px", color: "#1e293b", lineHeight: "1.6" }}
          >
            {renderQuestionWithAnswer()}
          </div>

          {isAttempted && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
                  border: `1px solid ${isCorrect ? "#86efac" : "#fecaca"}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                }}
              >
                {isCorrect ? (
                  <CheckCircleIcon
                    style={{ fontSize: "16px", color: "#22c55e" }}
                  />
                ) : (
                  <CancelIcon style={{ fontSize: "16px", color: "#ef4444" }} />
                )}
                <span style={{ color: isCorrect ? "#166534" : "#991b1b" }}>
                  <strong>Your Answer:</strong> {userAnswer.givenAnswer}
                </span>
              </div>

              {!isCorrect && (
                <div
                  style={{
                    padding: "10px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#dcfce7",
                    border: "1px solid #86efac",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                  }}
                >
                  <CheckCircleIcon
                    style={{ fontSize: "16px", color: "#22c55e" }}
                  />
                  <span style={{ color: "#166534" }}>
                    <strong>Correct Answer:</strong> {userAnswer.correctAnswer}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {isAttempted && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 10px",
              borderRadius: "16px",
              fontSize: "12px",
              fontWeight: "500",
              backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
              color: isCorrect ? "#166534" : "#991b1b",
              flexShrink: 0,
            }}
          >
            {isCorrect ? (
              <>
                <CheckCircleIcon style={{ fontSize: "14px" }} /> Correct
              </>
            ) : (
              <>
                <CancelIcon style={{ fontSize: "14px" }} /> Wrong
              </>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

// Component to render MCQ question with answer display
const MCQQuestion = ({ question, userAnswer, config }) => {
  const isAttempted = !!userAnswer;
  const isCorrect = userAnswer?.isCorrect;
  const options = question.options || [];

  return (
    <div
      style={{
        marginBottom: "20px",
        padding: "16px",
        borderRadius: "10px",
        backgroundColor: !isAttempted
          ? "#f8fafc"
          : isCorrect
          ? "#f0fdf4"
          : "#fef2f2",
        border: `1px solid ${
          !isAttempted ? "#e2e8f0" : isCorrect ? "#86efac" : "#fecaca"
        }`,
      }}
    >
      {/* Question Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "28px",
            height: "28px",
            borderRadius: "50%",
            backgroundColor: config.color,
            color: "white",
            fontSize: "12px",
            fontWeight: "600",
            flexShrink: 0,
          }}
        >
          Q{question.questionId}
        </span>
        <div style={{ flex: 1 }}>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: "500",
              color: "#1e293b",
            }}
            dangerouslySetInnerHTML={{ __html: question.question }}
          />
        </div>
        {isAttempted && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "4px 10px",
              borderRadius: "16px",
              fontSize: "12px",
              fontWeight: "500",
              backgroundColor: isCorrect ? "#dcfce7" : "#fee2e2",
              color: isCorrect ? "#166534" : "#991b1b",
              flexShrink: 0,
            }}
          >
            {isCorrect ? (
              <>
                <CheckCircleIcon style={{ fontSize: "14px" }} /> Correct
              </>
            ) : (
              <>
                <CancelIcon style={{ fontSize: "14px" }} /> Wrong
              </>
            )}
          </span>
        )}
      </div>

      {/* Options */}
      <div style={{ marginLeft: "40px" }}>
        {options.map((option, optIndex) => {
          const optionLabel =
            option.label?.toLowerCase() || String.fromCharCode(97 + optIndex);
          const optionText = option.text || option;
          const isUserAnswer =
            userAnswer?.givenAnswer?.toLowerCase() === optionLabel;
          const isCorrectOption =
            question.correctOption?.toLowerCase() === optionLabel;

          return (
            <div
              key={optIndex}
              style={{
                padding: "12px 16px",
                marginBottom: "8px",
                borderRadius: "8px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: isCorrectOption
                  ? "#dcfce7"
                  : isUserAnswer && !isCorrect
                  ? "#fee2e2"
                  : "#fff",
                border: `2px solid ${
                  isCorrectOption
                    ? "#22c55e"
                    : isUserAnswer && !isCorrect
                    ? "#ef4444"
                    : "#e2e8f0"
                }`,
                color: "#374151",
              }}
            >
              <span
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isCorrectOption
                    ? "#22c55e"
                    : isUserAnswer && !isCorrect
                    ? "#ef4444"
                    : "#e2e8f0",
                  color:
                    isCorrectOption || (isUserAnswer && !isCorrect)
                      ? "white"
                      : "#64748b",
                  fontSize: "13px",
                  fontWeight: "600",
                  flexShrink: 0,
                }}
              >
                {optionLabel.toUpperCase()}
              </span>
              <span
                style={{ flex: 1 }}
                dangerouslySetInnerHTML={{
                  __html: optionText.replace
                    ? optionText.replace(/<[^>]*>/g, "")
                    : optionText,
                }}
              />
              {isCorrectOption && (
                <CheckCircleIcon
                  style={{ fontSize: "20px", color: "#22c55e" }}
                />
              )}
              {isUserAnswer && !isCorrect && (
                <CancelIcon style={{ fontSize: "20px", color: "#ef4444" }} />
              )}
              {isUserAnswer && (
                <span
                  style={{
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    backgroundColor: isCorrect ? "#166534" : "#991b1b",
                    color: "white",
                    fontWeight: "500",
                  }}
                >
                  Your Answer
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main component to render test content - detects test type and renders appropriate view
const TestContentView = ({
  moduleData,
  moduleConfig: config,
  onExplainRequest,
  loadingExplanations,
  explanations,
}) => {
  const test = moduleData.test;
  const answers = moduleData.answers || [];

  // Handle Writing module separately (no test structure, shows submission data)
  if (config.key === "writingResult") {
    // Task definitions from test (for left side - shows instructions and task prompts)
    const testTasks = moduleData.test?.tasks || [];
    
    const writingData = {
      sections: moduleData.test?.sections || [],
      writingPrompt: moduleData.test?.writingPrompt || "",
      instructions: moduleData.test?.instructions || "",
      title: moduleData.test?.title || "",
      test: moduleData.test,
      // Use test.tasks for task definitions (left side display)
      writingTasks: testTasks,
    };

    // Student submission tasks (for right side - shows submitted images/text)
    const submissionTasks = moduleData.writingTasks || [];
    
    // Aggregate images and text from all tasks
    let allWritingImages = [];
    let allWritingText = "";
    let isPictureBased = false;
    let checkingStatus = moduleData.checkingStatus || "";
    
    if (submissionTasks.length > 0) {
      // Extract data from writingTasks array
      submissionTasks.forEach((task, index) => {
        if (task.writingImage && task.writingImage.length > 0) {
          allWritingImages = [...allWritingImages, ...task.writingImage];
          isPictureBased = true;
        }
        if (task.writingText) {
          allWritingText += (index > 0 ? "\n\n--- Task " + (index + 1) + " ---\n\n" : "") + task.writingText;
        }
        // Use the first non-null checking status
        if (!checkingStatus && task.checkingStatus) {
          checkingStatus = task.checkingStatus;
        }
      });
    }

    const submissionData = {
      writingText: allWritingText || moduleData.writingText || "",
      writingImage: allWritingImages.length > 0 ? allWritingImages : (moduleData.writingImage || []),
      isPictureBased: isPictureBased || (moduleData.writingImage?.length > 0),
      evaluationPdf: moduleData.accessorFeedBackInPdf?.pdfUrl || "",
      marks: moduleData.score ?? moduleData.obtainedMarks,
      grade: moduleData.grade || "",
      adminComments: moduleData.accessorFeedBack || "",
      createdAt: moduleData.createdAt || "",
      checkingStatus: checkingStatus || moduleData.checkingStatus || "",
      evaluatorId: moduleData.evaluatorId || null,
      // Include submission tasks for right side display
      writingTasks: submissionTasks,
    };

    return (
      <WritingSectionReview
        writingData={writingData}
        submissionData={submissionData}
      />
    );
  }

  // Handle Speaking module separately
  if (config.key === "speakingResult") {
    const speakingData = {
      mainCards: moduleData.test?.mainCards || [],
      title: moduleData.test?.title || "",
      instructions: moduleData.test?.instructions || "",
      test: moduleData.test,
    };

    const submissionData = {
      speakingAudios: moduleData.speakingAudios || [],
      evaluationPdf: moduleData.accessorFeedBackInPdf?.pdfUrl || "",
      marks: moduleData.score ?? moduleData.obtainedMarks,
      grade: moduleData.grade || "",
      adminComments: moduleData.accessorFeedBack || "",
      createdAt: moduleData.createdAt || "",
      checkingStatus: moduleData.checkingStatus || "",
      evaluatorId: moduleData.evaluatorId || null,
      evaluationType: moduleData.evaluationType || "",
    };

    return (
      <SpeakingSectionReview
        speakingData={speakingData}
        submissionData={submissionData}
      />
    );
  }

  if (!test) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
        <DescriptionIcon
          style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "16px" }}
        />
        <p>Test content not available for review</p>
      </div>
    );
  }

  // Transform answers to the format expected by review components
  const transformedAnswers = answers.map((a) => ({
    questionId: a.questionId,
    isCorrect: a.isCorrect,
    studentAnswer: a.givenAnswer || a.studentAnswer,
    correctAnswer: a.correctAnswer,
    explanation: a.explanation,
  }));

  // Detect if this is a listening test (has listeningParts, sections with audio, or parts with extracts structure)
  const isListeningTest =
    test.listeningParts ||
    (test.sections && test.sections.some((section) => section.audioUrl)) ||
    (test.parts &&
      test.parts.some((part) => part.extracts && part.extracts.length > 0));

  if (isListeningTest) {
    // Format listening data - handle both OET (parts) and IELTS (sections) formats
    const listeningData = test.sections
      ? { sections: test.sections }
      : test.parts
      ? { parts: test.parts }
      : test;

    return (
      <ListeningSectionReview
        listeningData={listeningData}
        answers={transformedAnswers}
        onExplainRequest={onExplainRequest}
        loadingExplanations={loadingExplanations}
        explanations={explanations}
      />
    );
  }

  // Transform test data for ReadingSectionReview
  // For IELTS reading, questions need to be attached to parts
  let readingData = test;
  
  if (test.parts && test.parts.length > 0) {
    // IELTS format - parts have paragraph text but questions come from answers
    // Create a lookup map of answers by partName (sequence)
    const answersByPartIndex = {};
    answers.forEach((answer) => {
      if (!answersByPartIndex[answer.questionId]) {
        answersByPartIndex[answer.questionId] = answer;
      }
    });

    // Enhance parts with question information
    const enhancedParts = test.parts.map((part, idx) => {
      // Normalize common fields so the review UI can detect part label and heading
      const normalizedPart = {
        ...part,
        partName: part.partName || part.part || undefined,
        heading: part.heading || part.partHeading || part.textTitle || undefined,
        instructions: part.instructions || undefined,
        paragraph: part.paragraph || undefined,
      };

      // Determine a reliable part label (A, B, C) for descriptions and UI
      const partLabelFromOrder = typeof normalizedPart.order === 'number' && normalizedPart.order >= 1 && normalizedPart.order <= 26
        ? String.fromCharCode(64 + normalizedPart.order) // 1 -> A
        : null;

      const partLabel =
        normalizedPart.part ||
        normalizedPart.partName ||
        partLabelFromOrder ||
        (idx < 3 ? ['A', 'B', 'C'][idx] : `P${idx + 1}`);

      normalizedPart.part = partLabel;

      // Prefer questions coming with the part (from test.parts[i].questions).
      // If missing, fall back to creating lightweight question objects from answers.
      const partQuestions = Array.isArray(normalizedPart.questions) && normalizedPart.questions.length > 0
        ? normalizedPart.questions
        : answers
            .filter((a) => typeof a.questionId === "number" && a.question)
            .map((a) => ({
              question: a.question,
              questionId: a.questionId,
              questionType: a.questionType || "unknown",
              options: a.options || [],
              correctAnswers: a.correctAnswers && a.correctAnswers.length ? a.correctAnswers : a.correctAnswer ? [a.correctAnswer] : [],
            }));

      // If this part provides a single paragraph (`paragraph`) use it to build `texts` for the review UI
      const texts = Array.isArray(normalizedPart.texts) && normalizedPart.texts.length > 0
        ? normalizedPart.texts
        : normalizedPart.paragraph
        ? [
            {
              title: normalizedPart.textTitle || normalizedPart.partHeading || normalizedPart.heading || `Passage ${idx + 1}`,
              paragraph: normalizedPart.paragraph,
              imageUrl: normalizedPart.imageUrl,
              questions: partQuestions,
            },
          ]
        : [];

      // Ensure heading fallback is present so Title shows useful text
      const heading = normalizedPart.heading || normalizedPart.partHeading || `Passage ${idx + 1}`;

      return {
        ...normalizedPart,
        part: partLabel,
        heading,
        questions: partQuestions.length > 0 ? partQuestions : [],
        texts,
      };
    });

    readingData = {
      parts: enhancedParts,
      oetReadingPart: enhancedParts,
    };
  } else {
    readingData = {
      parts: test.parts || [],
      oetReadingPart: test.parts || [],
    };
  }

  return (
    <ReadingSectionReview
      readingData={readingData}
      answers={transformedAnswers}
      onExplainRequest={onExplainRequest}
      loadingExplanations={loadingExplanations}
      explanations={explanations}
    />
  );
};

function AttemptResultPage() {
  const { testId, attemptId } = useParams();
  const navigate = useNavigate();
  const { getAttemptsUrl } = useTestNavigation();
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingExplanations, setLoadingExplanations] = useState({});
  const [explanations, setExplanations] = useState({});

  const { data, isLoading, isError } =
    useGetTestAttemptAllResultsQuery(attemptId);

  // API transformResponse already extracts data.data, so we get { result: {...} }
  const result = data?.result;

  // Handler for AI explanation requests
  const handleExplainRequest = useCallback(
    async (questionId, questionText, studentAnswer, correctAnswer) => {
      // Skip if already loading or already have explanation
      if (loadingExplanations[questionId] || explanations[questionId]) return;

      setLoadingExplanations((prev) => ({ ...prev, [questionId]: true }));

      try {
        // Use OpenAI API to generate explanation
        const explanation = await generateExplanationWithRetry(
          questionText,
          studentAnswer,
          correctAnswer
        );

        setExplanations((prev) => ({ ...prev, [questionId]: explanation }));
      } catch (error) {
        console.error("Failed to get explanation:", error);
        setExplanations((prev) => ({
          ...prev,
          [questionId]:
            "Unable to generate explanation. Please try again later.",
        }));
      } finally {
        setLoadingExplanations((prev) => ({ ...prev, [questionId]: false }));
      }
    },
    [loadingExplanations, explanations]
  );

  const handleBack = () => {
    navigate(getAttemptsUrl(testId));
  };

  const getGradeColor = (grade) => {
    // Use brand color variations for all grades
    const colors = {
      "A+": "#166534",
      A: "#16a34a",
      "A-": "#22c55e",
      "B+": BRAND_PRIMARY,
      B: BRAND_PRIMARY,
      "B-": BRAND_LIGHT,
      "C+": BRAND_PRIMARY,
      C: BRAND_LIGHT,
      "C-": "#e57373",
      D: "#ef5350",
      E: "#e53935",
      F: "#c62828",
    };
    return colors[grade] || BRAND_PRIMARY;
  };

  const calculatePercentage = (obtained, total) => {
    if (!total) return 0;
    return Math.round((obtained / total) * 100);
  };

  const calculateScorePercentage = (moduleData) => {
    // For reading/listening: calculate based on correct answers out of attempted questions
    if (
      moduleData.totalAttemptedQuestion &&
      moduleData.correctCount !== undefined
    ) {
      return Math.round(
        (moduleData.correctCount / moduleData.totalAttemptedQuestion) * 100
      );
    }
    // Fallback: calculate based on marks
    if (moduleData.obtainedMarks && moduleData.totalMarks) {
      return Math.round(
        (moduleData.obtainedMarks / moduleData.totalMarks) * 100
      );
    }
    return 0;
  };

  const getAvailableModules = () => {
    if (!result) return [];
    return Object.entries(moduleConfig)
      .filter(
        ([_, config]) =>
          result[config.key] && Object.keys(result[config.key]).length > 0
      )
      .map(([key, config]) => ({
        ...config,
        id: key,
        data: result[config.key],
      }));
  };

  const getTotalScore = () => {
    const modules = getAvailableModules();
    let totalObtained = 0;
    let totalMarks = 0;
    modules.forEach((module) => {
      totalObtained += module.data.obtainedMarks || 0;
      totalMarks += module.data.totalMarks || 0;
    });
    return { totalObtained, totalMarks };
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            border: `4px solid ${BRAND_LIGHT}`,
            borderTopColor: BRAND_PRIMARY,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <p style={{ color: "#64748b", fontSize: "14px" }}>Loading results...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isError || !result) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            backgroundColor: "#fee2e2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: "24px", color: "#dc2626" }}>!</span>
        </div>
        <p style={{ color: "#dc2626", fontSize: "16px", fontWeight: "500" }}>
          Failed to load results
        </p>
        <button
          onClick={handleBack}
          style={{
            padding: "10px 20px",
            backgroundColor: BRAND_PRIMARY,
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const availableModules = getAvailableModules();
  const { totalObtained, totalMarks } = getTotalScore();
  const overallPercentage = calculatePercentage(totalObtained, totalMarks);

  return (
    <div
      style={{ padding: "8px", maxWidth: "100%", margin: "0" }}
    >
      {/* Compact Header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_DARK} 100%)`,
          borderRadius: "12px",
          padding: "12px 16px",
          marginBottom: "12px",
          color: "white",
          boxShadow: "0 4px 20px rgba(157, 34, 53, 0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* Left side - Back button and title */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssessmentIcon style={{ fontSize: "20px" }} />
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "700",
                  lineHeight: 1.2,
                }}
              >
                Test Results
              </h1>
              <p style={{ margin: 0, opacity: 0.85, fontSize: "11px" }}>
                Complete test review
              </p>
            </div>
          </div>

          {/* Right side - Score */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                borderRadius: "10px",
                padding: "8px 14px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "10px", opacity: 0.85 }}>
                Overall Score
              </div>
              <div style={{ fontSize: "18px", fontWeight: "700" }}>
                {totalObtained} / {totalMarks}
              </div>
              <div
                style={{
                  fontSize: "10px",
                  opacity: 0.8,
                  padding: "2px 6px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "10px",
                  display: "inline-block",
                  marginTop: "2px",
                }}
              >
                {overallPercentage}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Module Score Cards - Compact */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "10px",
          marginBottom: "12px",
        }}
      >
        {availableModules.map((module) => {
          const Icon = module.icon;
          const percentage = calculatePercentage(
            module.data.obtainedMarks,
            module.data.totalMarks
          );

          return (
            <div
              key={module.id}
              style={{
                backgroundColor: "white",
                borderRadius: "10px",
                padding: "14px 16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                border: `1px solid ${module.bgColor}`,
                cursor: "pointer",
                transition: "all 0.2s ease",
                borderLeft: `3px solid ${module.color}`,
              }}
              onClick={() => setActiveTab(module.id)}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: module.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon style={{ fontSize: "18px", color: module.color }} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1e293b",
                      }}
                    >
                      {module.name}
                    </div>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "3px",
                        padding: "1px 6px",
                        borderRadius: "4px",
                        backgroundColor:
                          getGradeColor(module.data.band || module.data.grade) + "20",
                        color: getGradeColor(module.data.band || module.data.grade),
                        fontSize: "10px",
                        fontWeight: "600",
                        marginTop: "2px",
                      }}
                    >
                      <GradeIcon style={{ fontSize: "11px" }} />
                      Band: {module.data.band || module.data.grade || "N/A"}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {module.data.obtainedMarks || 0}
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        fontWeight: "400",
                      }}
                    >
                      /{module.data.totalMarks || 0}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      justifyContent: "flex-end",
                      marginTop: "2px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#16a34a",
                        fontWeight: "500",
                      }}
                    >
                      <CheckCircleIcon
                        style={{
                          fontSize: "12px",
                          verticalAlign: "middle",
                          marginRight: "2px",
                        }}
                      />
                      {module.data.correctAnswers || 0}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#dc2626",
                        fontWeight: "500",
                      }}
                    >
                      <CancelIcon
                        style={{
                          fontSize: "12px",
                          verticalAlign: "middle",
                          marginRight: "2px",
                        }}
                      />
                      {module.data.wrongAnswers || 0}
                    </span>
                  </div>
                </div>
              </div>
              {/* Progress bar */}
              <div
                style={{
                  marginTop: "10px",
                  height: "4px",
                  backgroundColor: "#f1f5f9",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percentage}%`,
                    height: "100%",
                    backgroundColor: module.color,
                    borderRadius: "2px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs Navigation */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "20px",
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: "0",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setActiveTab("overview")}
          style={{
            padding: "12px 20px",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            color: activeTab === "overview" ? BRAND_PRIMARY : "#64748b",
            borderBottom:
              activeTab === "overview"
                ? `2px solid ${BRAND_PRIMARY}`
                : "2px solid transparent",
            marginBottom: "-1px",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <TrendingUpIcon style={{ fontSize: "18px" }} />
          Overview
        </button>
        {availableModules.map((module) => {
          const Icon = module.icon;
          return (
            <button
              key={module.id}
              onClick={() => setActiveTab(module.id)}
              style={{
                padding: "12px 20px",
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                color: activeTab === module.id ? module.color : "#64748b",
                borderBottom:
                  activeTab === module.id
                    ? `2px solid ${module.color}`
                    : "2px solid transparent",
                marginBottom: "-1px",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Icon style={{ fontSize: "18px" }} />
              {module.name}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {activeTab === "overview" ? (
          <div style={{ padding: "24px" }}>
            <h3
              style={{
                margin: "0 0 20px 0",
                color: "#1e293b",
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              Performance Overview
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              {availableModules.map((module) => {
                const Icon = module.icon;

                return (
                  <div
                    key={module.id}
                    style={{
                      padding: "20px",
                      borderRadius: "10px",
                      backgroundColor: module.bgColor,
                      border: `1px solid ${module.color}30`,
                      cursor: "pointer",
                    }}
                    onClick={() => setActiveTab(module.id)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        marginBottom: "12px",
                      }}
                    >
                      <Icon style={{ fontSize: "20px", color: module.color }} />
                      <span style={{ fontWeight: "600", color: "#1e293b" }}>
                        {module.name}
                      </span>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px",
                        fontSize: "13px",
                      }}
                    >
                      <div>
                        <span style={{ color: "#64748b" }}>Score:</span>
                        <div style={{ fontWeight: "600", color: "#1e293b" }}>
                          {module.data.obtainedMarks ??
                            module.data.score ??
                            "-"}
                          /{module.data.totalMarks ?? "-"}
                        </div>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Band:</span>
                        <div
                          style={{
                            fontWeight: "600",
                            color: getGradeColor(module.data.band || module.data.grade),
                          }}
                        >
                          {module.data.band || module.data.grade || "N/A"}
                        </div>
                      </div>
                      {/* Show Correct/Wrong/Attempted/Percentage for Listening and Reading */}
                      {module.key !== "writingResult" &&
                        module.key !== "speakingResult" && (
                          <>
                            <div>
                              <span style={{ color: "#64748b" }}>
                                Attempted:
                              </span>
                              <div
                                style={{ fontWeight: "600", color: "#1e293b" }}
                              >
                                {module.data.totalAttemptedQuestion || 0}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: "#64748b" }}>Total:</span>
                              <div
                                style={{ fontWeight: "600", color: "#1e293b" }}
                              >
                                {module.data.totalQuestions || 0}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: "#64748b" }}>Correct:</span>
                              <div
                                style={{ fontWeight: "600", color: "#10b981" }}
                              >
                                {module.data.correctCount || 0}
                              </div>
                            </div>
                            <div>
                              <span style={{ color: "#64748b" }}>
                                Incorrect:
                              </span>
                              <div
                                style={{ fontWeight: "600", color: "#ef4444" }}
                              >
                                {module.data.wrongCount || 0}
                              </div>
                            </div>
                            {/* REMOVED: Score % section for IELTS tests */}
                          </>
                        )}
                      {/* Show Status for Writing and Speaking */}
                      {(module.key === "writingResult" ||
                        module.key === "speakingResult") && (
                        <div style={{ gridColumn: "span 2" }}>
                          <span style={{ color: "#64748b" }}>Status:</span>
                          <div
                            style={{
                              fontWeight: "600",
                              color:
                                module.data.checkingStatus === "checked"
                                  ? "#10b981"
                                  : module.data.checkingStatus === "pending"
                                  ? "#f59e0b"
                                  : "#64748b",
                            }}
                          >
                            {module.data.checkingStatus === "checked"
                              ? "✓ Evaluated"
                              : module.data.checkingStatus === "pending"
                              ? "⏳ Pending"
                              : module.data.checkingStatus || "N/A"}
                          </div>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: "16px",
                        padding: "10px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        textAlign: "center",
                        fontSize: "13px",
                        color: module.color,
                        fontWeight: "500",
                      }}
                    >
                      Click to view full test
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Quick Answer Summary */}
            <div>
              <h4
                style={{
                  margin: "0 0 16px 0",
                  color: "#1e293b",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
              >
                Quick Answer Summary
              </h4>
              {availableModules.map((module) => {
                const answers = module.data.answers || [];
                if (answers.length === 0) return null;

                return (
                  <div key={module.id} style={{ marginBottom: "20px" }}>
                    <h5
                      style={{
                        margin: "0 0 12px 0",
                        color: module.color,
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <module.icon style={{ fontSize: "18px" }} />
                      {module.name} Answers
                    </h5>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                      }}
                    >
                      {answers.map((answer, idx) => (
                        <div
                          key={idx}
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                            fontWeight: "700",
                            backgroundColor: answer.isCorrect
                              ? "#dcfce7"
                              : "#fff",
                            color: answer.isCorrect ? "#166534" : "#991b1b",
                            border: `2px solid ${
                              answer.isCorrect ? "#16a34a" : "#dc2626"
                            }`,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                          title={`Q${answer.questionId}: ${
                            answer.isCorrect ? "Correct" : "Wrong"
                          }`}
                          onMouseOver={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                            e.currentTarget.style.backgroundColor =
                              answer.isCorrect ? "#16a34a" : "#dc2626";
                            e.currentTarget.style.color = "#fff";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.backgroundColor =
                              answer.isCorrect ? "#dcfce7" : "#fff";
                            e.currentTarget.style.color = answer.isCorrect
                              ? "#166534"
                              : "#991b1b";
                          }}
                        >
                          {answer.questionId}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Individual Module Detail View with Full Test
          (() => {
            const module = availableModules.find((m) => m.id === activeTab);
            if (!module) return null;

            const Icon = module.icon;

            return (
              <div>
                {/* Module Header */}
                <div
                  style={{
                    padding: "24px",
                    borderBottom: "1px solid #e2e8f0",
                    backgroundColor: module.bgColor,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    <Icon style={{ fontSize: "28px", color: module.color }} />
                    <h3
                      style={{ margin: 0, color: "#1e293b", fontSize: "20px" }}
                    >
                      {module.name} - Complete Test Review
                    </h3>
                  </div>

                  <div
                    style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}
                  >
                    <div
                      style={{
                        padding: "12px 20px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginBottom: "4px",
                        }}
                      >
                        Score
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#1e293b",
                        }}
                      >
                        {module.data.obtainedMarks ??
                          module.data.score ??
                          "N/A"}
                        /{module.data.totalMarks ?? "-"}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "12px 20px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginBottom: "4px",
                        }}
                      >
                        Band
                      </div>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: getGradeColor(module.data.band || module.data.grade),
                        }}
                      >
                        {module.data.band || module.data.grade || "N/A"}
                      </div>
                    </div>

                    {/* Only show Correct/Wrong/Attempted for Listening and Reading modules */}
                    {module.key !== "writingResult" &&
                      module.key !== "speakingResult" && (
                        <>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Attempted
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#64748b",
                              }}
                            >
                              {module.data.totalAttemptedQuestion || 0}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Total
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#64748b",
                              }}
                            >
                              {module.data.totalQuestions || 0}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Correct
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#10b981",
                              }}
                            >
                              {module.data.correctCount || 0}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "white",
                              borderRadius: "8px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                marginBottom: "4px",
                              }}
                            >
                              Incorrect
                            </div>
                            <div
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#ef4444",
                              }}
                            >
                              {module.data.wrongCount || 0}
                            </div>
                          </div>
                          {/* REMOVED: Score % section for IELTS tests */}
                        </>
                      )}

                    {/* Show Evaluation Status for Writing and Speaking modules */}
                    {(module.key === "writingResult" ||
                      module.key === "speakingResult") && (
                      <div
                        style={{
                          padding: "12px 20px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            marginBottom: "4px",
                          }}
                        >
                          Status
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color:
                              module.data.checkingStatus === "checked"
                                ? "#10b981"
                                : module.data.checkingStatus === "pending"
                                ? "#f59e0b"
                                : "#64748b",
                          }}
                        >
                          {module.data.checkingStatus === "checked"
                            ? "✓ Evaluated"
                            : module.data.checkingStatus === "pending"
                            ? "⏳ Pending"
                            : module.data.checkingStatus || "N/A"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Full Test Content */}
                <TestContentView
                  moduleData={module.data}
                  moduleConfig={module}
                  onExplainRequest={handleExplainRequest}
                  loadingExplanations={loadingExplanations}
                  explanations={explanations}
                />
              </div>
            );
          })()
        )}
      </div>

      {/* Print Styles */}
      <style>
        {`
                    @media print {
                        body { 
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        button { display: none !important; }
                        .no-print { display: none !important; }
                    }
                `}
      </style>
    </div>
  );
}

export default AttemptResultPage;
